import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  Instagram, 
  Link2, 
  Save, 
  User, 
  Globe, 
  Twitter,
  Facebook,
  Youtube,
  Sparkles,
  Wand2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import { errorDetail } from '@/utils/apiError';
import { buildBioTemplate } from '@/utils/bioTemplate';
import { normalizeWebsite } from '@/utils/socials';
import StyleBookGallery from '@/components/StyleBookGallery';
import CredentialsSection from '@/components/CredentialsSection';
import SocialLinkPreview from '@/components/SocialLinkPreview';
import GoldenScissors from '@/components/GoldenScissors';
import OndaProgressO from '@/components/OndaProgressO';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';

export default function StylistProfilePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedDialog, setSavedDialog] = useState(false);
  const [savedStats, setSavedStats] = useState({ socials: 0, photos: 0, docs: 0 });
  const [portfolio, setPortfolio] = useState([]);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [meta, setMeta] = useState({ skills: [], years: null }); // feeds the bio template
  const [profile, setProfile] = useState({
    bio: '',
    instagram: '',
    twitter: '',
    facebook: '',
    youtube: '',
    website: '',
    tiktok: ''
  });

  useEffect(() => {
    fetchProfile();
    fetchPortfolio();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- one-shot mount hydration

  const fetchProfile = async () => {
    try {
      const response = await api.get('/stylists/profile/me');
      const data = response.data;
      // bio + social_links live at the TOP LEVEL of /stylists/profile/me — reading
      // them under .profile left this form blank and Save wiped saved socials.
      setProfile({
        bio: data.bio || '',
        instagram: data.social_links?.instagram || '',
        twitter: data.social_links?.twitter || '',
        facebook: data.social_links?.facebook || '',
        youtube: data.social_links?.youtube || '',
        website: data.social_links?.website || '',
        tiktok: data.social_links?.tiktok || ''
      });
      setMeta({ skills: data.skills || [], years: data.years_experience });
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Failed to load profile. You can still edit and save.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/stylists/profile/update', {
        bio: profile.bio,
        social_links: {
          instagram: profile.instagram.trim(),
          twitter: profile.twitter.trim(),
          facebook: profile.facebook.trim(),
          youtube: profile.youtube.trim(),
          // Repairs mistyped schemes too ("https:/x" → "https://x", no doubling).
          website: normalizeWebsite(profile.website),
          tiktok: profile.tiktok.trim()
        }
      });
      // Saved — show the scissors moment with their live progress, then walk
      // them straight toward verification & payouts.
      const socialCount = [profile.instagram, profile.tiktok, profile.twitter,
        profile.facebook, profile.youtube, profile.website].filter((v) => v && v.trim()).length;
      let photos = 0, docs = 0;
      try {
        const { data } = await api.get('/stylists/verification-progress');
        photos = data.photos_uploaded || 0;
        docs = data.documents_uploaded || 0;
      } catch (e) { /* counts are decorative here */ }
      setSavedStats({ socials: socialCount, photos, docs });
      setSavedDialog(true);
    } catch (error) {
      toast.error(errorDetail(error, 'Failed to save profile'));
      console.error('Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setProfile(prev => ({ ...prev, [field]: value }));
  };

  const writeBioForMe = () => {
    const userData = JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user') || '{}');
    const draft = buildBioTemplate({
      firstName: userData.first_name || userData.name?.split(' ')[0],
      years: meta.years,
      skills: meta.skills,
    });
    if (profile.bio.trim() && !window.confirm('Replace your current bio with a fresh draft?')) return;
    setProfile(prev => ({ ...prev, bio: draft }));
    toast.success('Bio drafted — make it yours! Replace the [brackets] with your details.');
  };

  // ── Style Book = the REAL portfolio (server-backed; same pool as onboarding) ──
  const fetchPortfolio = async () => {
    try {
      const { data } = await api.get('/credentials/portfolio');
      setPortfolio(data.map((p) => ({ id: p.photo_id, url: p.photo_url, name: p.caption || '' })));
    } catch (e) { /* gallery just shows empty */ }
  };

  const handlePortfolioUpload = async (files) => {
    setUploadingPhotos(true);
    let uploaded = 0;
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append('photo', file);
        fd.append('photo_type', 'work_sample');
        await api.post('/credentials/portfolio/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        uploaded += 1;
      } catch (e) {
        toast.error(errorDetail(e, `Could not upload ${file.name}`));
      }
    }
    if (uploaded > 0) toast.success(`Added ${uploaded} photo${uploaded !== 1 ? 's' : ''}`);
    try {
      await fetchPortfolio();
    } finally {
      setUploadingPhotos(false);
    }
  };

  const handlePortfolioRemove = async (photo) => {
    setPortfolio((prev) => prev.filter((p) => p.id !== photo.id));
    try {
      await api.delete(`/credentials/portfolio/${photo.id}`);
      toast.success('Photo removed');
    } catch (e) {
      toast.error('Could not remove the photo');
    } finally {
      await fetchPortfolio();
    }
  };

  const handlePortfolioReorder = async (ordered) => {
    setPortfolio(ordered);
    try {
      await api.put('/credentials/portfolio/reorder', { photo_ids: ordered.map((p) => p.id) });
    } catch (e) {
      toast.error('Could not save the new order');
      await fetchPortfolio();
    }
  };

  const handlePortfolioClear = async () => {
    const ids = portfolio.map((p) => p.id);
    setPortfolio([]);
    try {
      await Promise.allSettled(ids.map((id) => api.delete(`/credentials/portfolio/${id}`)));
      toast.success('All photos removed');
    } finally {
      await fetchPortfolio();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-6">
      <div className="container mx-auto max-w-4xl">
        <div className="mb-8">
          <h1 className="font-playfair text-4xl font-bold text-primary mb-2">
            Your Profile
          </h1>
          <p className="text-muted-foreground">
            Customize your bio, social links, and style book to showcase your work
          </p>
        </div>

        <div className="space-y-8">
          <Card className="border border-border/40">
            <CardHeader>
              <CardTitle className="font-playfair text-xl flex items-center gap-2">
                <User className="h-5 w-5 text-primary" />
                About You
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="bio" className="text-sm font-medium block">
                    Bio
                  </Label>
                  <Button type="button" variant="outline" size="sm" onClick={writeBioForMe}>
                    <Wand2 className="mr-2 h-4 w-4" /> Write it for me
                  </Button>
                </div>
                <Textarea
                  id="bio"
                  placeholder="Tell clients about yourself, your experience, and what makes you unique..."
                  value={profile.bio}
                  onChange={(e) => handleInputChange('bio', e.target.value)}
                  className="h-[280px] resize-none overflow-y-auto bg-background border-border/40"
                  maxLength={1000}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {profile.bio.length}/1000 characters
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border/40">
            <CardHeader>
              <CardTitle className="font-playfair text-xl flex items-center gap-2">
                <Link2 className="h-5 w-5 text-primary" />
                Social Links
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="instagram" className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Instagram className="h-4 w-4 text-pink-500" />
                    Instagram
                  </Label>
                  <Input
                    id="instagram"
                    placeholder="@yourusername"
                    value={profile.instagram}
                    onChange={(e) => handleInputChange('instagram', e.target.value)}
                    className="bg-background border-border/40"
                  />
                  <SocialLinkPreview network="instagram" value={profile.instagram} />
                </div>

                <div>
                  <Label htmlFor="tiktok" className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-cyan-400" />
                    TikTok
                  </Label>
                  <Input
                    id="tiktok"
                    placeholder="@yourusername"
                    value={profile.tiktok}
                    onChange={(e) => handleInputChange('tiktok', e.target.value)}
                    className="bg-background border-border/40"
                  />
                  <SocialLinkPreview network="tiktok" value={profile.tiktok} />
                </div>

                <div>
                  <Label htmlFor="twitter" className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Twitter className="h-4 w-4 text-blue-400" />
                    Twitter / X
                  </Label>
                  <Input
                    id="twitter"
                    placeholder="@yourusername"
                    value={profile.twitter}
                    onChange={(e) => handleInputChange('twitter', e.target.value)}
                    className="bg-background border-border/40"
                  />
                  <SocialLinkPreview network="twitter" value={profile.twitter} />
                </div>

                <div>
                  <Label htmlFor="facebook" className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Facebook className="h-4 w-4 text-blue-600" />
                    Facebook
                  </Label>
                  <Input
                    id="facebook"
                    placeholder="facebook.com/yourpage"
                    value={profile.facebook}
                    onChange={(e) => handleInputChange('facebook', e.target.value)}
                    className="bg-background border-border/40"
                  />
                  <SocialLinkPreview network="facebook" value={profile.facebook} />
                </div>

                <div>
                  <Label htmlFor="youtube" className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Youtube className="h-4 w-4 text-red-500" />
                    YouTube
                  </Label>
                  <Input
                    id="youtube"
                    placeholder="youtube.com/@yourchannel"
                    value={profile.youtube}
                    onChange={(e) => handleInputChange('youtube', e.target.value)}
                    className="bg-background border-border/40"
                  />
                  <SocialLinkPreview network="youtube" value={profile.youtube} />
                </div>

                <div>
                  <Label htmlFor="website" className="text-sm font-medium mb-2 flex items-center gap-2">
                    <Globe className="h-4 w-4 text-primary" />
                    Website
                  </Label>
                  <Input
                    id="website"
                    placeholder="https://yourwebsite.com"
                    value={profile.website}
                    onChange={(e) => handleInputChange('website', e.target.value)}
                    className="bg-background border-border/40"
                  />
                  <SocialLinkPreview network="website" value={profile.website} />
                </div>
              </div>

              <p className="text-xs text-muted-foreground pt-2">
                Tap each link to confirm it's really your account — confirmed socials power your
                Discover page and future auto-features.
              </p>
            </CardContent>
          </Card>

          <StyleBookGallery
            photos={portfolio}
            onUpload={handlePortfolioUpload}
            onRemove={handlePortfolioRemove}
            onReorder={handlePortfolioReorder}
            onClearAll={handlePortfolioClear}
            uploading={uploadingPhotos}
            maxPhotos={50}
          />

          <CredentialsSection />

          <div className="flex justify-center gap-4">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary px-8"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Profile
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={savedDialog} onOpenChange={setSavedDialog}>
        <DialogContent className="max-w-md text-center">
          <DialogHeader>
            <div className="mx-auto mb-2">
              <GoldenScissors size="lg" />
            </div>
            <DialogTitle className="font-cormorant text-3xl text-primary text-center">
              Profile saved — looking sharp
            </DialogTitle>
          </DialogHeader>
          <div className="mx-auto flex flex-col gap-2 text-left py-2">
            <OndaProgressO met={savedStats.socials >= 1} label={`${savedStats.socials} social${savedStats.socials === 1 ? '' : 's'} linked`} />
            <OndaProgressO met={savedStats.photos >= 3} label={`${savedStats.photos} photo${savedStats.photos === 1 ? '' : 's'} added`} />
            <OndaProgressO met={savedStats.docs >= 3} label={`${savedStats.docs} accreditation${savedStats.docs === 1 ? '' : 's'} under review`} />
          </div>
          <p className="text-sm text-muted-foreground">
            Onda isn't just On Demand — you're on your way to getting <strong>paid and verified</strong>.
          </p>
          <div className="flex flex-col gap-2 pt-2">
            <Button className="btn-primary w-full" onClick={() => { setSavedDialog(false); navigate('/onboarding'); }}>
              Continue to verification &amp; payouts →
            </Button>
            <Button variant="ghost" className="w-full" onClick={() => setSavedDialog(false)}>
              Keep editing
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
